/**
 * pages/Rentals.jsx — Rental Management Page (V2)
 *
 * Full rental lifecycle: list, create, return.
 * Uses ui.jsx components + CSS variables exclusively.
 * Fetches from GET /api/rentals, POST /api/rentals, POST /api/rentals/{id}/return.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { rentals as rentalsApi, fleet as fleetApi } from '../services/api';
import { formatShortDate, formatNumber } from '../utils/formatters';
import {
  PageHeader, Badge, Card, SearchInput, EmptyState,
  SkeletonTable, StatCard, GlassModal,
} from '../components/ui';

// ── Status config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  active:    { labelKey: 'rentals.statusActive',    variant: 'info' },
  reserved:  { labelKey: 'rentals.statusReserved',  variant: 'warning' },
  late:      { labelKey: 'rentals.statusLate',      variant: 'danger' },
  completed: { labelKey: 'rentals.statusCompleted', variant: 'neutral' },
  cancelled: { labelKey: 'rentals.statusCancelled', variant: 'neutral' },
  disputed:  { labelKey: 'rentals.statusDisputed',  variant: 'danger' },
};

// ── Filter pills ─────────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',       labelKey: 'rentals.all' },
  { key: 'active',    labelKey: 'rentals.active' },
  { key: 'late',      labelKey: 'rentals.overdue' },
  { key: 'completed', labelKey: 'rentals.completed' },
  { key: 'cancelled', labelKey: 'rentals.cancelled' },
];

// ── Helpers ──────────────────────────────────────────────────────────
function computeDays(start, end) {
  if (!start || !end) return 0;
  return Math.max(1, Math.ceil((new Date(end) - new Date(start)) / 86400000));
}

function maskPhone(phone) {
  if (!phone || phone.length < 4) return '—';
  return '•••• ' + phone.slice(-4);
}

function formatMAD(amount) {
  if (amount == null) return '—';
  return formatNumber(amount) + ' MAD';
}

// ── Filter pill button ───────────────────────────────────────────────
function FilterPill({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        fontSize: 'var(--text-caption)',
        fontWeight: active ? 600 : 400,
        background: active ? 'var(--gold-dim)' : 'transparent',
        color: active ? 'var(--gold)' : 'var(--text-secondary)',
        border: `1px solid ${active ? 'var(--border-gold)' : 'var(--border)'}`,
        borderRadius: 20,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all var(--duration-fast) var(--ease-smooth)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-mono)',
          color: active ? 'var(--gold-light)' : 'var(--text-muted)',
        }}>{count}</span>
      )}
    </button>
  );
}

// ── Return Modal ─────────────────────────────────────────────────────
function ReturnModal({ open, rental, onClose, onSubmit, submitting }) {
  const { t } = useTranslation();
  const [endMileage, setEndMileage] = useState('');
  const [fuelLevel, setFuelLevel] = useState('100');
  const [damageNotes, setDamageNotes] = useState('');
  const [extraCharges, setExtraCharges] = useState('0');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setEndMileage(''); setFuelLevel('100'); setDamageNotes(''); setExtraCharges('0'); setError(null); }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    if (!endMileage || parseInt(endMileage) < 0) { setError(t('rentals.mileageRequired')); return; }
    try {
      await onSubmit(rental.id, {
        end_mileage: parseInt(endMileage),
        fuel_level_pct: parseInt(fuelLevel) || 100,
        damage_notes: damageNotes || null,
        extra_charges_mad: parseFloat(extraCharges) || 0,
        extra_charges_reason: damageNotes || null,
      });
    } catch (e) { setError(e.message || t('rentals.returnError')); }
  };

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px', background: 'var(--elevated)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text)', fontSize: 'var(--text-body)', fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = {
    fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block',
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('rentals.returnVehicle')} subtitle={rental ? `${rental.plate_number} — ${rental.client_name}` : ''}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {error && (
          <div style={{ padding: 'var(--space-md)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-body)' }}>{error}</div>
        )}
        <div>
          <label style={labelStyle}>{t('rentals.returnMileage')}</label>
          <input type="number" value={endMileage} onChange={e => setEndMileage(e.target.value)} placeholder="Ex: 45800" style={inputStyle} />
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('rentals.fuelPct')}</label>
            <input type="number" value={fuelLevel} onChange={e => setFuelLevel(e.target.value)} min="0" max="100" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('rentals.extraCharges')}</label>
            <input type="number" value={extraCharges} onChange={e => setExtraCharges(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t('rentals.damageNotes')}</label>
          <textarea value={damageNotes} onChange={e => setDamageNotes(e.target.value)} rows={3} placeholder={t('rentals.describeDamage')} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
        </div>
      </div>
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px', height: 44, background: submitting ? 'var(--text-muted)' : 'var(--green)', color: 'var(--bg)', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
          {submitting ? t('common.processing') : t('rentals.confirmReturn')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── New Rental Modal ─────────────────────────────────────────────────
function NewRentalModal({ open, onClose, onSubmit, submitting, availableVehicles }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ vehicle_id: '', client_name: '', client_cin: '', client_phone: '', start_date: '', end_date: '', daily_rate: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ vehicle_id: '', client_name: '', client_cin: '', client_phone: '', start_date: new Date().toISOString().split('T')[0], end_date: '', daily_rate: '' });
      setError(null);
    }
  }, [open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleVehicleChange = (vehicleId) => {
    set('vehicle_id', vehicleId);
    const v = availableVehicles.find(av => av.id === vehicleId);
    if (v) {
      const rates = { economy: 250, standard: 350, premium: 500, suv: 600, luxury: 800 };
      set('daily_rate', String(rates[v.category] || 300));
    }
  };

  const days = form.start_date && form.end_date ? Math.max(1, Math.ceil((new Date(form.end_date) - new Date(form.start_date)) / 86400000)) : 0;
  const total = days * (parseFloat(form.daily_rate) || 0);

  const handleSubmit = async () => {
    setError(null);
    if (!form.vehicle_id) { setError(t('rentals.vehicleRequired')); return; }
    if (!form.client_name.trim()) { setError(t('rentals.clientRequired')); return; }
    if (!form.client_cin.trim()) { setError(t('rentals.cinRequired')); return; }
    if (!form.start_date || !form.end_date) { setError(t('rentals.datesRequired')); return; }
    if (new Date(form.end_date) <= new Date(form.start_date)) { setError(t('rentals.endAfterStart')); return; }
    try { await onSubmit(form); } catch (e) { setError(e.message || t('rentals.createError')); }
  };

  const inputStyle = {
    width: '100%', height: 44, padding: '0 12px', background: 'var(--elevated)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text)', fontSize: 'var(--text-body)', fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = {
    fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block',
  };

  return (
    <GlassModal open={open} onClose={onClose} title={t('rentals.newRental')} subtitle={t('rentals.newRentalDesc')} width={520}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {error && (
          <div style={{ padding: 'var(--space-md)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-body)' }}>{error}</div>
        )}
        <div>
          <label style={labelStyle}>{t('rentals.vehicle')}</label>
          <select value={form.vehicle_id} onChange={e => handleVehicleChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">{t('rentals.selectVehicle')}</option>
            {availableVehicles.map(v => (
              <option key={v.id} value={v.id}>{v.plate_number} — {v.make} {v.model} ({v.year})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t('rentals.clientName')}</label>
          <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder={t('rentals.clientName')} style={inputStyle} />
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('rentals.cin')}</label>
            <input value={form.client_cin} onChange={e => set('client_cin', e.target.value)} placeholder="AB123456" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('rentals.phone')}</label>
            <input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} placeholder="06 12 34 56 78" style={inputStyle} />
          </div>
        </div>
        <div className="modal-form-row">
          <div>
            <label style={labelStyle}>{t('rentals.startDate')}</label>
            <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t('rentals.endDate')}</label>
            <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t('rentals.dailyRateLabel')}</label>
          <input type="number" value={form.daily_rate} onChange={e => set('daily_rate', e.target.value)} placeholder="350" style={inputStyle} />
        </div>
        {days > 0 && form.daily_rate && (
          <div style={{ padding: 'var(--space-md)', background: 'var(--gold-dim)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-sm)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>{days} {t('rentals.days')} × {formatNumber(parseFloat(form.daily_rate))} MAD</span>
            <span style={{ fontSize: 'var(--text-title)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--gold)' }}>{formatNumber(total)} MAD</span>
          </div>
        )}
      </div>
      <div style={{ padding: 'var(--space-lg) var(--space-xl)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)' }}>
        <button onClick={onClose} style={{ padding: '10px 20px', height: 44, background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)', cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px', height: 44, background: submitting ? 'var(--text-muted)' : 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em' }}>
          {submitting ? t('common.creating') : t('rentals.createRental')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── No mock data: API failure shows empty state + error banner ───────

// ── Main Component ───────────────────────────────────────────────────
export default function Rentals() {
  const { t } = useTranslation();
  const [rentalsList, setRentalsList] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [returnModal, setReturnModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const [rentalsRes, vehiclesRes] = await Promise.allSettled([
        rentalsApi.list(50),
        fleetApi.vehicles('available'),
      ]);

      if (ctrl.signal.aborted) return;

      if (rentalsRes.status === 'fulfilled') {
        setRentalsList(rentalsRes.value?.rentals || []);
      }
      if (vehiclesRes.status === 'fulfilled') {
        setAvailableVehicles(vehiclesRes.value?.vehicles || []);
      }

      if (rentalsRes.status === 'rejected' && vehiclesRes.status === 'rejected') {
        setRentalsList([]);
        setError(rentalsRes.reason?.message || vehiclesRes.reason?.message || t('system.serviceUnavailable'));
      } else {
        setError(null);
      }
    } catch (e) {
      if (!ctrl.signal.aborted) {
        setError(e.message);
        setRentalsList([]);
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  const handleReturn = async (rentalId, data) => {
    setSubmitting(true);
    try {
      await rentalsApi.returnRental(rentalId, data);
      setReturnModal(null);
      fetchData();
    } finally { setSubmitting(false); }
  };

  const handleCreate = async (form) => {
    setSubmitting(true);
    try {
      const vehicle = availableVehicles.find(v => v.id === form.vehicle_id);
      await rentalsApi.create({
        vehicle_id: form.vehicle_id,
        client_cin: form.client_cin.trim(),
        client_name: form.client_name.trim(),
        client_phone: form.client_phone?.trim() || null,
        branch_id: vehicle?.branch_id || form.vehicle_id,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        daily_rate_mad: parseFloat(form.daily_rate),
        deposit_amount_mad: 0,
        deposit_type: 'trust',
      });
      setModalOpen(false);
      fetchData();
    } finally { setSubmitting(false); }
  };

  // Local filtering + sort (late first, then active, then others)
  const STATUS_PRIORITY = { late: 0, active: 1, reserved: 2, completed: 3, cancelled: 4 };
  const filtered = rentalsList
    .filter(r => {
      if (filter !== 'all') {
        if (filter === 'active' && r.status !== 'active' && r.status !== 'reserved') return false;
        if (filter === 'late' && r.status !== 'late') return false;
        if (filter === 'completed' && r.status !== 'completed') return false;
        if (filter === 'cancelled' && r.status !== 'cancelled') return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.client_name} ${r.plate_number} ${r.make} ${r.model}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => (STATUS_PRIORITY[a.status] ?? 5) - (STATUS_PRIORITY[b.status] ?? 5));

  const stats = {
    total: rentalsList.length,
    active: rentalsList.filter(r => r.status === 'active' || r.status === 'reserved').length,
    late: rentalsList.filter(r => r.status === 'late').length,
    completed: rentalsList.filter(r => r.status === 'completed').length,
    cancelled: rentalsList.filter(r => r.status === 'cancelled').length,
  };

  const counts = { all: stats.total, active: stats.active, late: stats.late, completed: stats.completed, cancelled: stats.cancelled };

  const totalRevenue = rentalsList.filter(r => r.status === 'active' || r.status === 'completed').reduce((s, r) => s + (Number(r.total_price_mad) || 0), 0);

  const addButton = (
    <button onClick={() => setModalOpen(true)} style={{
      padding: '10px 20px', height: 44,
      background: 'var(--gold)',
      color: 'var(--bg)',
      border: 'none',
      borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-body)', fontWeight: 700,
      cursor: 'pointer',
      fontFamily: 'inherit', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    }}>{`+ ${t('rentals.newRental')}`}</button>
  );

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      <PageHeader title={t('rentals.title')} subtitle={`${stats.total} ${t('common.total').toLowerCase()} · ${stats.active} ${t('rentals.active').toLowerCase()}`} action={addButton} />

      {/* Stat cards */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        <StatCard label={t('common.total')} value={stats.total} index={0} />
        <StatCard label={t('rentals.active')} value={stats.active} trendUp={true} index={1} />
        <StatCard
          label={t('rentals.overdue')}
          value={stats.late}
          trendUp={stats.late > 0 ? false : undefined}
          index={2}
        />
        <StatCard
          label={t('rentals.completed')}
          value={stats.completed}
          trend={totalRevenue > 0 ? `${formatNumber(totalRevenue)} MAD` : undefined}
          trendUp={totalRevenue > 0 ? true : undefined}
          index={3}
        />
      </div>

      {/* Filter bar */}
      <div className="lazy-section" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xl)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flex: 1, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <FilterPill key={f.key} label={t(f.labelKey)} count={counts[f.key]} active={filter === f.key} onClick={() => setFilter(f.key)} />
          ))}
        </div>
        <SearchInput value={search} onChange={setSearch} placeholder={t('rentals.searchPlaceholder')} style={{ width: 240, flexShrink: 0 }} />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 'var(--space-lg)', background: 'var(--status-alert-bg)', border: '1px solid var(--red)', borderRadius: 'var(--radius)', color: 'var(--red)', fontSize: 'var(--text-body)', marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{t('common.errorPrefix')} {error}</span>
          <button onClick={() => { setError(null); setLoading(true); fetchData(); }} style={{ padding: '6px 16px', background: 'transparent', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', fontSize: 'var(--text-caption)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{t('common.retry')}</button>
        </div>
      )}

      {/* Loading */}
      {loading && <SkeletonTable rows={6} />}

      {/* Empty */}
      {!loading && filtered.length === 0 && rentalsList.length === 0 && (
        <Card>
          <EmptyState icon={null} title={t('rentals.noRentals')} description={t('rentals.noRentalsDesc')} ctaLabel={t('rentals.newRental')} onCta={() => setModalOpen(true)} />
        </Card>
      )}

      {/* No search results */}
      {!loading && filtered.length === 0 && rentalsList.length > 0 && (
        <Card>
          <EmptyState icon={null} title={t('common.noResults')} description={t('rentals.noResultsDesc')} ctaLabel={t('common.reset')} onCta={() => { setFilter('all'); setSearch(''); }} />
        </Card>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="responsive-table" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[t('rentals.client'), t('rentals.vehicle'), t('rentals.period'), t('rentals.amount'), t('common.status'), t('common.actions')].map(h => (
                  <th key={h} style={{ padding: '12px 16px', fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const days = computeDays(r.start_date, r.end_date);
                const isLate = r.status === 'late';
                const canReturn = r.status === 'active' || r.status === 'reserved' || r.status === 'late';
                const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.completed;

                return (
                  <tr key={r.id || i} style={{
                    borderLeft: isLate ? '3px solid var(--red)' : '3px solid transparent',
                    transition: 'background var(--duration-fast) var(--ease-smooth)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td data-label={t('rentals.client')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 'var(--text-body)', fontWeight: 500, color: 'var(--text)' }}>{r.client_name || '—'}</div>
                      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{maskPhone(r.client_phone)}</div>
                    </td>
                    <td data-label={t('rentals.vehicle')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 'var(--text-body)', color: 'var(--text)' }}>{r.make} {r.model}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--gold)', fontSize: 'var(--text-caption)', letterSpacing: 1 }}>{r.plate_number}</div>
                    </td>
                    <td data-label={t('rentals.period')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 'var(--text-body)', color: 'var(--text)' }}>
                      <div>{formatShortDate(r.start_date)} → {formatShortDate(r.actual_return_date || r.end_date)}</div>
                      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{days} {t('rentals.days')}</div>
                    </td>
                    <td data-label={t('rentals.amount')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>
                      {r.total_price_mad ? formatNumber(r.total_price_mad) : '—'}
                      <div style={{ fontSize: 'var(--text-caption)', fontWeight: 400, color: 'var(--text-muted)' }}>{r.daily_price_mad ? `${formatNumber(r.daily_price_mad)}${t('common.perDay')}` : ''}</div>
                    </td>
                    <td data-label={t('common.status')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      <Badge label={t(sc.labelKey)} variant={sc.variant} />
                    </td>
                    <td data-label={t('common.actions')} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                      {canReturn && (
                        <button onClick={() => setReturnModal(r)} style={{
                          padding: '6px 14px', background: 'transparent',
                          border: '1px solid var(--gold)',
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--gold)',
                          fontSize: 'var(--text-caption)',
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit', letterSpacing: '0.02em',
                          transition: 'all var(--duration-fast) var(--ease-smooth)',
                          opacity: 1,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.color = 'var(--bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--gold)'; }}>
                          {t('rentals.return')}
                        </button>
                      )}
                      {r.status === 'completed' && <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary bar */}
      {!loading && rentalsList.length > 0 && (
        <div style={{
          marginTop: 'var(--space-lg)', padding: 'var(--space-md) var(--space-xl)',
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-sm)',
          fontSize: 'var(--text-body)', color: 'var(--text-secondary)',
        }}>
          <div style={{ display: 'flex', gap: 'var(--space-lg)' }}>
            <span><strong style={{ color: 'var(--text)' }}>{stats.active}</strong> {t('rentals.summaryActive')}</span>
            {stats.late > 0 && <span style={{ color: 'var(--red)' }}>· <strong>{stats.late}</strong> {t('rentals.summaryLate')}</span>}
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--gold)' }}>{t('common.total')} : {formatNumber(totalRevenue)} MAD</span>
        </div>
      )}

      {/* Modals */}
      <NewRentalModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} submitting={submitting} availableVehicles={availableVehicles} />
      <ReturnModal open={!!returnModal} rental={returnModal} onClose={() => setReturnModal(null)} onSubmit={handleReturn} submitting={submitting} />
    </div>
  );
}
